import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, Save, Users, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ErnieProPricingSettings() {
  const [settings, setSettings] = useState(null);
  const [monthlyPrice, setMonthlyPrice] = useState(5.99);
  const [isFree, setIsFree] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/ernie-pro-settings`, {
        withCredentials: true
      });
      setSettings(response.data);
      setMonthlyPrice(response.data.monthly_price || 5.99);
      setIsFree(response.data.is_free || false);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load Ernie Pro settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/ernie-pro-settings`,
        {
          monthly_price: parseFloat(monthlyPrice),
          is_free: isFree,
          stripe_product_id: settings?.stripe_product_id,
          stripe_price_id: settings?.stripe_price_id
        },
        { withCredentials: true }
      );
      setSettings(response.data);
      toast.success('✓ Ernie Pro pricing updated!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E8934D]"></div>
        </div>
      </div>
    );
  }

  const hasChanges = 
    (settings && (
      parseFloat(monthlyPrice) !== settings.monthly_price ||
      isFree !== settings.is_free
    ));

  return (
    <div className="admin-card">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Crown className="w-8 h-8 text-yellow-500" />
        <div>
          <h2 className="text-2xl font-bold text-white">Ernie AI Pro Subscription</h2>
          <p className="text-sm text-gray-300">Configure pricing for Pro access</p>
        </div>
      </div>

      {/* Active Subscribers */}
      {settings?.active_subscribers !== undefined && (
        <div className="mb-6 p-4 bg-green-900/30 border-2 border-green-500 rounded-lg">
          <div className="flex items-center gap-2 text-green-100">
            <Users className="w-5 h-5" />
            <span className="font-bold">{settings.active_subscribers}</span>
            <span>Active Pro Subscribers</span>
          </div>
        </div>
      )}

      {/* Pricing Settings */}
      <div className="space-y-6">
        {/* Monthly Price */}
        <div>
          <label className="block text-white font-bold text-lg mb-3">
            Monthly Price:
          </label>
          <div className="relative">
            <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="number"
              value={monthlyPrice}
              onChange={(e) => setMonthlyPrice(e.target.value)}
              min="0"
              step="0.01"
              disabled={isFree}
              className="w-full pl-12 pr-4 py-3 border-2 border-[#8B6F47] rounded-lg focus:outline-none focus:border-[#E8934D] text-gray-900 text-lg disabled:bg-gray-200 disabled:cursor-not-allowed"
            />
          </div>
          <p className="text-sm text-gray-300 mt-2">
            Users will pay this amount monthly for Pro access
          </p>
        </div>

        {/* Make Free Toggle */}
        <div className="p-4 bg-blue-900/30 border-2 border-blue-500 rounded-lg">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isFree}
              onChange={(e) => setIsFree(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex-1">
              <span className="text-white font-bold block">Make Pro Free for Everyone</span>
              <span className="text-blue-200 text-sm">
                Enable this to grant Pro access without payment (no Stripe subscription)
              </span>
            </div>
          </label>
        </div>

        {/* Stripe Info */}
        {!isFree && (
          <div className="p-4 bg-purple-900/30 border-2 border-purple-500 rounded-lg">
            <p className="text-purple-100 text-sm">
              <strong>Note:</strong> Make sure you have created a Stripe Product and Price for Ernie AI Pro
              in your Stripe dashboard. Update the Stripe IDs in your environment if needed.
            </p>
          </div>
        )}

        {/* Save Button */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex-1 retro-button flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
          </button>
          
          {hasChanges && (
            <button
              onClick={() => {
                setMonthlyPrice(settings.monthly_price);
                setIsFree(settings.is_free);
              }}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Cancel
            </button>
          )}
        </div>

        {/* Current Status */}
        {settings && (
          <div className="p-3 bg-gray-800/50 border-2 border-gray-600 rounded-lg">
            <p className="text-gray-200 text-sm">
              <strong>Current Status:</strong>{' '}
              {settings.is_free ? (
                <span className="text-green-400">Free Mode (No Payment Required)</span>
              ) : (
                <span className="text-yellow-400">${settings.monthly_price}/month via Stripe</span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
